/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import NewInput from '../../Components/NewInput/NewInput';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import DateInput from '../../Components/DateInput/DateInput';
import { userAPI } from '../../services/apiService';
import './Profile.css';

function UserProfile() {
    const { t } = useTranslation();
    const [userData, setUserData] = useState({
        first_name: '',
        last_name: '',
        gender: '',
        phone_number: '',
        email: '',
        father_name: '',
        national_id: '',
        birth_date: '',
        country: '',
        province: '',
        postal_code: '',
        address: '',
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [passwords, setPasswords] = useState({
        current_password: '',
        new_password: '',
        confirm_new_password: '',
    });

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const data = await userAPI.getInfo();
                setUserData((prev) => ({
                    ...prev,
                    ...data,
                    birth_date: data.birth_date ? data.birth_date : '',
                }));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [t]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            await userAPI.updateInfo(userData);

            setSnackbar({
                open: true,
                message: t('profile.update_success'),
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.message || t('profile.update_error'),
                severity: 'error',
            });
        }
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const getGenderText = (gender) => {
        switch (gender) {
            case 'M':
                return t('profile.gender_male');
            case 'F':
                return t('profile.gender_female');
            default:
                return '';
        }
    };

    const handleOpenPasswordDialog = () => setPasswordDialogOpen(true);
    const handleClosePasswordDialog = () => {
        setPasswords({
            current_password: '',
            new_password: '',
            confirm_new_password: '',
        });
        setPasswordDialogOpen(false);
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswords((prev) => ({ ...prev, [name]: value }));
    };

    const handlePasswordSubmit = async () => {
        try {
            await userAPI.changePassword(passwords);

            setSnackbar({
                open: true,
                message: t('profile.password_change_success'),
                severity: 'success',
            });
            handleClosePasswordDialog();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.message || t('profile.password_change_error'),
                severity: 'error',
            });
        }
    };

    return (
        <div className="profile_page">
            <Sidebar />
            <div className="profile_main">
                <Navbar />

                <div className="profile_container">
                    <div className="profile_section read_only">
                        <h3>{t('profile.basic_info')}</h3>
                        <NewInput
                            label={t('profile.full_name')}
                            value={`${userData.first_name} ${userData.last_name}`}
                            readOnly
                        />
                        <NewInput
                            label={t('profile.gender')}
                            value={getGenderText(userData.gender)}
                            readOnly
                        />
                        <h3>{t('profile.contact_info')}</h3>
                        <NewInput
                            label={t('profile.mobile')}
                            value={userData.phone_number}
                            readOnly
                        />
                        <NewInput label={t('profile.email')} value={userData.email} readOnly />
                        <Button
                            variant="contained"
                            onClick={handleOpenPasswordDialog}
                            className="save_btn"
                        >
                            {t('profile.change_password')}
                        </Button>
                    </div>
                    <div className="profile_section editable">
                        <h3>{t('profile.additional_info')}</h3>
                        <NewInput
                            label={t('profile.father_name')}
                            name="father_name"
                            value={userData.father_name}
                            onChange={handleChange}
                        />
                        <NewInput
                            label={t('profile.national_id')}
                            name="national_id"
                            value={userData.national_id}
                            onChange={handleChange}
                        />
                        <DateInput
                            label={t('profile.birth_date')}
                            value={userData.birth_date}
                            onChange={(val) =>
                                setUserData((prev) => ({ ...prev, birth_date: val }))
                            }
                        />
                        <NewInput
                            label={t('profile.country')}
                            name="country"
                            value={userData.country}
                            onChange={handleChange}
                        />
                        <NewInput
                            label={t('profile.province')}
                            name="province"
                            value={userData.province}
                            onChange={handleChange}
                        />
                        <NewInput
                            label={t('profile.postal_code')}
                            name="postal_code"
                            value={userData.postal_code}
                            onChange={handleChange}
                        />
                        <NewInput
                            label={t('profile.address')}
                            name="address"
                            value={userData.address}
                            onChange={handleChange}
                        />
                        <Button variant="contained" onClick={handleSubmit} className="save_btn">
                            {t('profile.apply_changes')}
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                className="password_dialog"
            >
                <DialogTitle>{t('profile.change_password')}</DialogTitle>
                <DialogContent>
                    <p>{t('profile.current_password')}</p>
                    <TextField
                        fullWidth
                        type="password"
                        name="current_password"
                        value={passwords.current_password}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        margin="dense"
                    />
                    <p>{t('profile.new_password')}</p>
                    <TextField
                        fullWidth
                        type="password"
                        name="new_password"
                        value={passwords.new_password}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        margin="dense"
                    />
                    <p>{t('profile.confirm_new_password')}</p>
                    <TextField
                        fullWidth
                        type="password"
                        name="confirm_new_password"
                        value={passwords.confirm_new_password}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        margin="dense"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        color="success"
                        onClick={handlePasswordSubmit}
                        sx={{ fontWeight: 'bold' }}
                    >
                        {t('profile.confirm')}
                    </Button>
                    <Button
                        color="error"
                        onClick={handleClosePasswordDialog}
                        sx={{ fontWeight: 'bold' }}
                    >
                        {t('profile.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default UserProfile;
